import { useEffect, useRef } from 'react';
import { trailBonusRoute, type PlayerProfile, type SlotDemoScene } from '@goodtime/engine';
import type { SlotTrailState } from '@goodtime/shared';
import { useTrailGame } from '../../hooks/useTrailGame';

export interface SlotTrailProps {
  scene: SlotDemoScene | null;
  trail: SlotTrailState | null;
  /** Bumped by the host on each landed spin, so the panel can animate. */
  spinKey: number;
  /** Hold the board back this long after a hop (a big win shows first). */
  revealDelayMs: number;
  /** Stand aside: the slot loading, the first-spin hint, a gamble. */
  hidden: boolean;
  /** Plural, lower case: "gumdrops". */
  collectorNoun: string;
  /** The bonus should be on screen (after the hop onto it, or a tap on the gift). */
  bonusShown: boolean;
  onOpenBonus: () => void;
  /** Opens box `index` on the server: the trail after it and the wallet's coins. */
  onPick: (index: number) => Promise<{ trail: SlotTrailState; coins: number | null }>;
  /** The bonus has closed, with the trail as it ended (null: as it was,
   *  the gift still waiting). */
  onBonusClosed: (trail: SlotTrailState | null) => void;
  /** For the mini-game the bonus can roll instead of the boxes
   *  (trail_games): the server session and player, and the profile the
   *  gems it pays on opening are mirrored on. */
  sessionId: string | null;
  playerId: string | null;
  profile: Pick<PlayerProfile, 'awardGems'>;
}

/**
 * A slot's Sweet Trail (slots without the road), mounted by App while the
 * slot is on screen. It draws nothing itself: the panel and the
 * pick-a-prize bonus are drawn by the slot scene on its canvas (producer
 * 2026-10-14: everything in the game in Pixi), so this keeps the scene up
 * to date and plays the bonus when asked: the boxes, or the mini-game the
 * bonus rolled (useTrailGame). All state comes from the server.
 */
export function SlotTrail({
  scene,
  trail,
  spinKey,
  revealDelayMs,
  hidden,
  collectorNoun,
  bonusShown,
  onOpenBonus,
  onPick,
  onBonusClosed,
  sessionId,
  playerId,
  profile,
}: SlotTrailProps) {
  const latest = useRef({ onOpenBonus, onPick, onBonusClosed, revealDelayMs, scene });
  latest.current = { onOpenBonus, onPick, onBonusClosed, revealDelayMs, scene };
  const game = useTrailGame({
    sessionId,
    playerId,
    themeId: trail?.themeId ?? null,
    getScene: () => latest.current.scene,
    profile,
  });
  const playGame = game.play;

  useEffect(() => {
    scene?.setTrail(trail, spinKey, latest.current.revealDelayMs);
  }, [scene, trail, spinKey]);

  // Leaving the slot (or its trail): the panel goes with it.
  useEffect(() => {
    if (!scene) return;
    return () => scene.setTrail(null, 0);
  }, [scene]);

  useEffect(() => {
    scene?.setTrailChrome({ hidden, collectorNoun, notice: game.notice, onOpenBonus: () => latest.current.onOpenBonus() });
  }, [scene, hidden, collectorNoun, game.notice]);

  // The bonus, once each time it's asked for. A game left open (a reload)
  // waits for the slot to be on screen.
  const playing = useRef(false);
  const route = trail ? trailBonusRoute(trail) : null;
  const ready = bonusShown && route !== null && (route.kind !== 'resume' || !hidden);
  useEffect(() => {
    if (!scene || !ready || !trail || playing.current) return;
    playing.current = true;
    void (async () => {
      const result = await playGame(trail);
      if (!result) return;
      if ('closed' in result) {
        latest.current.onBonusClosed(result.closed);
        return;
      }
      const after = await scene.playTrailBonus(result.boxes, (index) => latest.current.onPick(index));
      latest.current.onBonusClosed(after);
    })().finally(() => {
      playing.current = false;
    });
    // Started once per opening: the board follows its own picks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, ready]);

  return null;
}
